import { faPlus } from '@fortawesome/free-solid-svg-icons';
import * as React from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { change } from 'redux-form';
import { EventVideoViewModel, UploadedVideoViewModel } from 'stageclip-common';

import {
  createAllEventVideosRequestedAction,
  createAllUploadedVideosRequestedAction,
} from '../../actions/eventVideosActions';
import { DataRequestActionTypes } from '../../constants/actionTypes';
import { FormId } from '../../constants/formId';
import { ClippingPlatformState } from '../../store/clippingPlatformState';
import { getAllWithIds, getItem } from '../../store/selectors';
import {
  baseMapDispatchToProps,
  baseMapStateToProps,
  ComponentBase,
  BaseDispatchProps,
  BaseStateProps,
} from '../componentBase';
import { AppSelectField } from '../form/appSelectField';
import { AppTextField } from '../form/appTextField';
import { required } from '../form/formValidations';
import FormModal from '../modal/formModal';

interface AddEventVideoFormOwnProps {
  eventId: string;
}

interface AddEventVideoFormStateProps extends BaseStateProps {
  formData: any;
  eventVideos: EventVideoViewModel[];
}

interface AddEventVideoFormDispatchProps extends BaseDispatchProps {
  changeFieldValue: (field: string, value: any) => void;
  loadAllUploadedEventVideos: (c: AddEventVideoForm) => void;
  loadAllEventVideos: (c: AddEventVideoForm) => void;
}

interface AddEventVideoFormInnerStateProps {
  uploadedVideos: UploadedVideoViewModel[];
}

class AddEventVideoForm extends ComponentBase<
  AddEventVideoFormOwnProps,
  AddEventVideoFormStateProps,
  AddEventVideoFormDispatchProps,
  AddEventVideoFormInnerStateProps
> {
  constructor(props: any) {
    super(props);
    this.state = { uploadedVideos: undefined };
    this.getInitialNameValue = this.getInitialNameValue.bind(this);
  }

  protected async loadData(props: any) {
    if (this.state.uploadedVideos === undefined) {
      await props.loadAllUploadedEventVideos(this);
    }

    if (props.eventVideos === undefined) {
      await props.loadAllEventVideos(this);
    }
  }

  protected dataLoaded(props: any) {
    return this.state.uploadedVideos !== undefined && props.eventVideos !== undefined;
  }

  protected loggedInRender() {
    return (
      <FormModal
        formId={FormId.AddEventVideoForm}
        formTitle="Add Event Video"
        buttonIcon={faPlus}
        submitActionType={DataRequestActionTypes.AddEventVideoRequested}
        reduxData={{ eventId: this.props.eventId }}
      >
        <AppTextField name="name" label="Name" validate={[required]} />
        <AppSelectField
          name="azurePath"
          label="Choose video"
          options={this.getEventVideoOptions()}
          validate={[required]}
          onChangeCallback={this.onChangeUploadedVideoCallback}
        />
      </FormModal>
    );
  }

  private getEventVideoOptions() {
    return this.state.uploadedVideos
      ? this.state.uploadedVideos
          .map((video: UploadedVideoViewModel) => ({ value: video.azurePath, label: video.name }))
          .filter(value => this.props.eventVideos.findIndex(e => e.azurePath === value.value) < 0)
      : [];
  }

  private getInitialNameValue() {
    return this.props.formData ? { ...this.props.formData, name: this.props.formData.azurePath } : undefined;
  }

  private onChangeUploadedVideoCallback = (event: any) => {
    this.props.changeFieldValue('name', event.label);
  };
}

function mapStateToProps(
  state: ClippingPlatformState,
  ownProps: AddEventVideoFormOwnProps,
): AddEventVideoFormStateProps {
  const form = state.form[FormId.AddEventVideoForm];
  const event =
    getItem(state.entities.events, ownProps.eventId) || getItem(state.entities.archivedEvents, ownProps.eventId);

  return {
    ...baseMapStateToProps(state),
    eventVideos: event.eventVideos ? getAllWithIds(state.entities.eventVideos, event.eventVideos) : undefined,
    formData: form ? form.values : undefined,
  };
}

function mapDispatchToProps(dispatch: Dispatch, ownProps: AddEventVideoFormOwnProps): AddEventVideoFormDispatchProps {
  return {
    ...baseMapDispatchToProps(dispatch),
    changeFieldValue: (field, value) => dispatch(change(FormId.AddEventVideoForm, field, value)),
    loadAllEventVideos: (c: AddEventVideoForm) =>
      dispatch(createAllEventVideosRequestedAction(ownProps.eventId, c, dispatch)),
    loadAllUploadedEventVideos: (c: AddEventVideoForm) =>
      dispatch(createAllUploadedVideosRequestedAction(ownProps.eventId, c, dispatch)),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(AddEventVideoForm);
